import {
  serializeFilterValues,
  type FilterField,
  type FilterValue,
  type FilterValues,
} from '@nucleus-commerce/admin-kit/index-table';
import {
  ProductFiltersFulfillmentModeItem,
  ProductFiltersStatusItem,
  ProductFiltersStatusNotItem,
  ProductFiltersStockItem,
  SchedulePlacementStateInput,
  type ProductFilters,
} from '@nucleus-commerce/api-client/admin';

/** Which product list a picker or a list screen reads: the master catalog or one store's. */
export type ProductPickerScope = { kind: 'org' } | { kind: 'store'; storeId: string };

/** `org` (`GET /org/products`) or `store` (`GET /stores/:id/products`). */
export type ProductScopeKind = ProductPickerScope['kind'];

/** Attribute filters are keyed `attr.<attribute key>`, as the list endpoints spell the param. */
export const ATTRIBUTE_FILTER_PREFIX = 'attr.';

export const attributeFilterKey = (attributeKey: string) =>
  `${ATTRIBUTE_FILTER_PREFIX}${attributeKey}`;

type Field = FilterField & { scope?: ProductScopeKind };

/**
 * Every filter of D-182 decision 4 as the query string spells it, with the scope it belongs to
 * when it is not shared. Sending another scope's param answers 422, so the converters below drop
 * them. Attribute filters (`attr.<key>`) are dynamic and handled apart.
 */
const PRODUCT_FILTER_FIELDS: readonly Field[] = [
  { key: 'status', type: 'enum', negatable: true },
  { key: 'productTypeId', type: 'async' },
  { key: 'collectionId', type: 'async' },
  { key: 'collectionId_none', type: 'boolean' },
  { key: 'tags', type: 'async' },
  { key: 'tagsAll', type: 'async' },
  { key: 'vendor', type: 'async' },
  { key: 'price', type: 'number-range' },
  { key: 'stock', type: 'enum' },
  { key: 'inStoreId', type: 'enum', scope: 'org' },
  { key: 'notInStoreId', type: 'enum', scope: 'org' },
  { key: 'inAnyStore', type: 'boolean', scope: 'org' },
  { key: 'needsAttention', type: 'boolean', scope: 'org' },
  { key: 'createdAt', type: 'date-range' },
  { key: 'updatedAt', type: 'date-range' },
  { key: 'publishedAt', type: 'date-range', scope: 'store' },
  { key: 'hasImage', type: 'boolean' },
  { key: 'scheduled', type: 'boolean', scope: 'org' },
  { key: 'scheduleState', type: 'enum', scope: 'store' },
  { key: 'scheduleId', type: 'async', scope: 'store' },
  { key: 'fulfillmentMode', type: 'enum' },
  { key: 'unpriced', type: 'boolean', scope: 'store' },
];

/** The filter keys a scope accepts (without the `attr.<key>` ones). */
export function productFilterKeys(scope: ProductScopeKind): string[] {
  return PRODUCT_FILTER_FIELDS.filter((f) => !f.scope || f.scope === scope).map((f) => f.key);
}

/**
 * A scope's filters as the param codec needs them (key, type, negatable), for a list route's
 * search schema and loader, plus an enum field per `attr.<key>` param present in `search`:
 * attribute keys are only known once the dictionary loads, and an enum and a yes/no filter
 * write the same param (`attr.metal=14k`, `attr.engravable=true`).
 */
export function productFilterFields(
  scope: ProductScopeKind,
  search: Readonly<Record<string, unknown>> = {},
): FilterField[] {
  const fields: FilterField[] = PRODUCT_FILTER_FIELDS.filter((f) => !f.scope || f.scope === scope);
  for (const key of Object.keys(search)) {
    if (key.startsWith(ATTRIBUTE_FILTER_PREFIX)) fields.push({ key, type: 'enum' });
  }
  return fields;
}

function fieldsFor(values: FilterValues, scope: ProductScopeKind): FilterField[] {
  const fields: FilterField[] = PRODUCT_FILTER_FIELDS.filter((f) => !f.scope || f.scope === scope);
  for (const [key, value] of Object.entries(values)) {
    if (!key.startsWith(ATTRIBUTE_FILTER_PREFIX)) continue;
    fields.push({ key, type: value.op === 'eq' ? 'boolean' : 'enum' });
  }
  return fields;
}

/**
 * Applied filters as list query params (`status=active,draft`, `price_gte=10000`,
 * `attr.metal=14k_white_gold`, `collectionId_none=true`), ready to spread into a list call.
 * Filters of the other scope and empty values are left out; `q`, `searchBy`, `sort` and paging
 * are the caller's.
 */
export function toProductListParams(
  values: FilterValues,
  scope: ProductScopeKind,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(serializeFilterValues(fieldsFor(values, scope), values)).filter(
      (entry): entry is [string, string] => entry[1] !== null,
    ),
  );
}

const listOf = (value: FilterValue | undefined): string[] | undefined =>
  value?.op === 'in' && value.values.length > 0 ? [...value.values] : undefined;

/** An enum filter's values that the endpoint knows (a stale URL value is dropped). */
function enumsOf<T extends string>(
  value: FilterValue | undefined,
  known: Readonly<Record<string, T>>,
): T[] | undefined {
  const allowed = new Set<string>(Object.values(known));
  return listOf(value)?.filter((item): item is T => allowed.has(item));
}

const oneOf = (value: FilterValue | undefined): string | undefined => listOf(value)?.[0];

const flagOf = (value: FilterValue | undefined): boolean | undefined =>
  value?.op === 'eq' ? value.value : undefined;

const boundsOf = (value: FilterValue | undefined) =>
  value?.op === 'between'
    ? {
        min: value.min === '' ? undefined : value.min,
        max: value.max === '' ? undefined : value.max,
      }
    : {};

function cents(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const number = Number(raw);
  return Number.isInteger(number) && number >= 0 ? number : undefined;
}

/** Drops undefined keys and empty arrays: the JSON form treats both as "no filter". */
function compact<T extends object>(filters: T): T {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && !(Array.isArray(value) && value.length === 0),
    ),
  ) as T;
}

/**
 * Applied filters in the JSON form a `ProductSelection` embeds (`ProductFilters`: camelCase,
 * arrays, `attr: { key: codes }`, prices in minor units). Filters of the other scope are dropped,
 * as `toProductListParams` drops them. Search text is not a filter value: add `q` / `searchBy`
 * yourself (`toProductSelection` does).
 */
export function toProductFilters(values: FilterValues, scope: ProductScopeKind): ProductFilters {
  const inScope = new Set(productFilterKeys(scope));
  const v = (key: string) => (inScope.has(key) ? values[key] : undefined);
  const status = v('status');
  const price = boundsOf(v('price'));
  const created = boundsOf(v('createdAt'));
  const updated = boundsOf(v('updatedAt'));
  const published = boundsOf(v('publishedAt'));

  const attr: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(values)) {
    if (!key.startsWith(ATTRIBUTE_FILTER_PREFIX)) continue;
    const codes = value.op === 'eq' ? [String(value.value)] : listOf(value);
    if (codes) attr[key.slice(ATTRIBUTE_FILTER_PREFIX.length)] = codes;
  }

  return compact<ProductFilters>({
    status: status?.op === 'in' ? enumsOf(status, ProductFiltersStatusItem) : undefined,
    statusNot:
      status?.op === 'not_in'
        ? enumsOf({ op: 'in', values: status.values }, ProductFiltersStatusNotItem)
        : undefined,
    productTypeId: listOf(v('productTypeId')),
    vendor: listOf(v('vendor')),
    tags: listOf(v('tags')),
    tagsAll: listOf(v('tagsAll')),
    collectionId: listOf(v('collectionId')),
    collectionNone: flagOf(v('collectionId_none')),
    priceGte: cents(price.min),
    priceLte: cents(price.max),
    stock: enumsOf(v('stock'), ProductFiltersStockItem),
    inStoreId: oneOf(v('inStoreId')),
    notInStoreId: oneOf(v('notInStoreId')),
    inAnyStore: flagOf(v('inAnyStore')),
    needsAttention: flagOf(v('needsAttention')),
    createdAtGte: created.min,
    createdAtLte: created.max,
    updatedAtGte: updated.min,
    updatedAtLte: updated.max,
    publishedAtGte: published.min,
    publishedAtLte: published.max,
    hasImage: flagOf(v('hasImage')),
    scheduled: flagOf(v('scheduled')),
    scheduleId: oneOf(v('scheduleId')),
    scheduleState: enumsOf(v('scheduleState'), SchedulePlacementStateInput),
    fulfillmentMode: enumsOf(v('fulfillmentMode'), ProductFiltersFulfillmentModeItem),
    unpriced: flagOf(v('unpriced')),
    attr: Object.keys(attr).length > 0 ? attr : undefined,
  });
}
